// hooks/useAnalytics.ts
// Analytics tracking and metrics using API

import { useState, useEffect, useCallback } from 'react';
import {
  trackEvent as apiTrackEvent,
  getAnalytics,
  type AnalyticsEventType,
  type AnalyticsMetrics,
  type AnalyticsResponse
} from '../lib/api';

interface TrackEventOptions {
  buildingId: string;
  eventType: AnalyticsEventType;
  unitId?: string;
  metadata?: Record<string, unknown>;
}

interface UseAnalyticsResult {
  trackEvent: (options: TrackEventOptions) => Promise<void>;
  trackQRScan: (buildingId: string) => Promise<void>;
  trackEmailClick: (buildingId: string, unitId?: string) => Promise<void>;
  trackPhoneClick: (buildingId: string, unitId?: string) => Promise<void>;
  trackUnitView: (buildingId: string, unitId: string) => Promise<void>;
  trackShare: (buildingId: string, unitId?: string, platform?: string) => Promise<void>;
}

interface UseAnalyticsMetricsOptions {
  buildingId: string;
  dateRange: '7d' | '30d' | '90d';
}

interface UseAnalyticsMetricsResult {
  metrics: AnalyticsMetrics | null;
  dailyData: Array<{ date: string; qr_scans: number; email_clicks: number; unit_views: number }>;
  totalEvents: number;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

// Hook for fetching analytics metrics (admin dashboard)
export function useAnalyticsMetrics({
  buildingId,
  dateRange
}: UseAnalyticsMetricsOptions): UseAnalyticsMetricsResult {
  const [data, setData] = useState<AnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchMetrics = useCallback(async () => {
    if (!buildingId) return;

    setLoading(true);
    setError(null);

    const { data: result, error: fetchError } = await getAnalytics(buildingId, dateRange);

    if (fetchError || !result) {
      setError(new Error(fetchError || 'Failed to fetch analytics'));
      setData(null);
    } else {
      setData(result);
    }

    setLoading(false);
  }, [buildingId, dateRange]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  return {
    metrics: data?.metrics || null,
    dailyData: data?.dailyData || [],
    totalEvents: data?.totalEvents || 0,
    loading,
    error,
    refetch: fetchMetrics
  };
}

// Hook for tracking analytics events
export function useAnalytics(): UseAnalyticsResult {
  const trackEvent = useCallback(async ({
    buildingId,
    eventType,
    unitId,
    metadata = {}
  }: TrackEventOptions): Promise<void> => {
    await apiTrackEvent(eventType, buildingId, unitId, metadata);
  }, []);

  const trackQRScan = useCallback(async (buildingId: string) => {
    await trackEvent({
      buildingId,
      eventType: 'qr_scan',
      metadata: {
        user_agent: navigator.userAgent,
        referrer: document.referrer
      }
    });
  }, [trackEvent]);

  const trackEmailClick = useCallback(async (buildingId: string, unitId?: string) => {
    await trackEvent({
      buildingId,
      eventType: 'email_click',
      unitId
    });
  }, [trackEvent]);

  const trackPhoneClick = useCallback(async (buildingId: string, unitId?: string) => {
    await trackEvent({
      buildingId,
      eventType: 'phone_click',
      unitId
    });
  }, [trackEvent]);

  const trackUnitView = useCallback(async (buildingId: string, unitId: string) => {
    await trackEvent({
      buildingId,
      eventType: 'unit_view',
      unitId
    });
  }, [trackEvent]);

  const trackShare = useCallback(async (buildingId: string, unitId?: string, platform?: string) => {
    await trackEvent({
      buildingId,
      eventType: 'share',
      unitId,
      metadata: { platform }
    });
  }, [trackEvent]);

  return {
    trackEvent,
    trackQRScan,
    trackEmailClick,
    trackPhoneClick,
    trackUnitView,
    trackShare
  };
}
